import axios from "axios";
import client from "@/libs/client.ts";
import type { Balance, Profile } from "@/types/account";
import { ProfileFactory } from "@/factories/ProfileFactory.ts";

// "Signed out" and "the backend is broken" are different answers: only the former is a 401/419.
const isUnauthenticated = (error: unknown): boolean =>
  axios.isAxiosError(error) && [401, 419].includes(error.response?.status ?? 0);

const check = async (): Promise<
  { balance: Balance; features: Feature[]; profile: Profile } | false
> => {
  try {
    const data = await client.get("auth/check");

    return {
      balance: data.data.data.balance,
      features: data.data.data.modules?.map(
        (module: { name: string; config: {} }): Feature => ({ name: module.name, isEnabled: true }),
      ),
      profile: new ProfileFactory().fromJson(data.data.data.user),
    };
  } catch (error) {
    // Swallowing every failure sent a 500 or an unreachable backend to the login screen and looked
    // exactly like an expired session.
    if (!isUnauthenticated(error)) {
      throw error;
    }

    return false;
  }
};

const login = async (email: string, password: string): Promise<Profile | false> => {
  const baseUrl = import.meta.env.VITE_BASE_URL;

  const setCsrfToken = await client.get(`${baseUrl}/sanctum/csrf-cookie`);

  if (setCsrfToken.status !== 204) {
    throw new Error("Failed to set CSRF token");
  }

  const auth = await client.post("auth/login", {
    email,
    password,
  });

  if (auth.status !== 200) {
    return false;
  }

  return new ProfileFactory().fromJson(auth.data.data);
};

const logout = async () => {
  // Logging out changes state, so it must not be a GET: Laravel exempts GET from CSRF, which left
  // it forgeable from any page with an <img> tag.
  const result = await client.post("auth/logout");

  return result.status === 204;
};

export { check, login, logout };
